<template>
  <RevCard class="pb-20 pt-24">
    <div v-if="hasError" class="mb-4 px-16 md:px-24">
      <RevInfoBlock
        data-qa="payment-form-info-block-error"
        data-test="payment-form-info-block-error"
        :icon="IconCrossInCircle"
        :title="i18n(translations.paymentFormErrorTitle)"
        variant="danger"
      >
        <FormattedMessage
          class="text-static-default-mid body-2"
          :definition="translations.paymentFormErrorContent"
        >
          <template #link>
            <RevLink
              data-qa="other-options-link"
              data-test="other-options-link"
              @click="exitXr"
              >{{ i18n(translations.paymentFormErrorLink) }}</RevLink
            >
          </template>
        </FormattedMessage>
      </RevInfoBlock>
    </div>

    <PaymentForm
      ref="paymentForm"
      :base-price
      class="!py-0"
      :createPayment
      data-qa="payment-form"
      data-test="payment-form"
      :payment-methods="paymentMethods ?? []"
      :resume-payment-id
      @setup-error="handleSetupErrors"
      @submit-error="handleSubmitErrors"
      @submit-success="handleSubmitSuccess"
    />
  </RevCard>
  <div class="mb-72 mt-32">
    <RevCheckbox
      id="express-replacement-tc"
      v-model="hasAcceptedTC"
      data-qa="payment-form-tc-checkbox"
      data-test="payment-form-tc-checkbox"
    >
      <template #label>
        <FormattedMessage :definition="translations.paymentFormTcCheckbox">
          <template #tc>
            <RevLink @click="goToExpressReplacementTC">
              {{ i18n(translations.paymentFormTcCheckboxLink) }}
            </RevLink>
          </template>
        </FormattedMessage>
      </template>
    </RevCheckbox>
    <PaymentSubmitButton
      class="mt-24"
      data-qa="payment-form-submit-button"
      data-test="payment-form-submit-button"
      :disabled="!hasAcceptedTC"
      :form="paymentForm"
      full-width="always"
      @click="trackSubmitButtonClick"
      >{{ i18n(translations.paymentFormSubmitButton) }}</PaymentSubmitButton
    >
    <div class="mt-16 flex md:mt-24">
      <IconLockLocked class="mr-8 shrink-0" />
      <FormattedMessage
        class="text-static-default-low body-2"
        :definition="translations.paymentFormPciDss"
      >
        <template #link>
          <RevLink
            data-qa="data-protection-link"
            data-test="data-protection-link"
            @click="goToDataProtection"
            >{{ i18n(translations.paymentFormPciDssLink) }}</RevLink
          >
        </template>
      </FormattedMessage>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  navigateTo,
  useRequestURL,
  useRoute,
  useRouteLocationWithLocale,
  useRouter,
} from '#imports'
import { computed, inject, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { Currency } from '@backmarket/http-api'
import {
  type PaymentMethod,
  PaymentMethodCode,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import {
  PaymentForm,
  type PaymentFormCreateFunctionOptions,
  type PaymentFormInstance,
  PaymentSubmitButton,
  type SubmitErrorEvent,
  type SubmitSuccessData,
  usePaymentRedirection,
} from '@backmarket/nuxt-layer-payment/form'
import { useMarketPaymentMethodsFor } from '@backmarket/nuxt-layer-payment/methods'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCard } from '@ds/components/Card'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'
import { IconLockLocked } from '@ds/icons/IconLockLocked'

import { CMS } from '~/scopes/cms/routes-names'
import { CUSTOMER_CARE_MODALS_NAMES } from '~/scopes/customer-care/common/modals/names'
import { ResolutionFlowStateMachineKey } from '~/scopes/customer-care/resolution/symbols'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'
import { LEGAL_PAGE_NAME } from '~/scopes/legal/constants'

import translations from './XrPaymentForm.translations'

const resolutionFlowStateMachine = inject(ResolutionFlowStateMachineKey)
const $httpFetch = useHttpFetch()
const router = useRouter()
const i18n = useI18n()
const { params, fullPath: paymentPagePath, query, name: routeName } = useRoute()
const { origin } = useRequestURL()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const { trackClick } = useTracking()

const confirmationPage = {
  name: CUSTOMER_CARE.RESOLUTION.RESOLUTION_EXPRESS_REPLACEMENT_CONFIRMATION,
  params: { resolutionId: params.resolutionId },
}
const { fullPath: confirmationPagePath } = router.resolve(confirmationPage)

const paymentForm = ref<PaymentFormInstance | null>(null)
const basePrice = ref({ amount: '0', currency: Currency.EUR })
const hasAcceptedTC = ref(false)
const hasError = ref(false)

const supportedPaymentMethodCodes: Array<PaymentMethodCode> = [
  PaymentMethodCode.CARD,
  PaymentMethodCode.FAKE,
]

const { data: paymentMethods } = await useMarketPaymentMethodsFor({
  tokenization: true,
  bagPrice: basePrice,
  customMethodFilter: (methods) =>
    methods.filter((method) =>
      supportedPaymentMethodCodes.includes(method.bmCode),
    ),
})

const resumePaymentId = computed(() =>
  query.paymentId ? String(query.paymentId) : undefined,
)

const handleSubmitErrors = ({ cause }: SubmitErrorEvent) => {
  if (cause?.type === '/errors/not-found') {
    resolutionFlowStateMachine?.send('ERROR')

    return
  }
  hasError.value = true
}

const handleSetupErrors = () => {
  resolutionFlowStateMachine?.send('ERROR')
}
const proceedRedirection = usePaymentRedirection({
  paymentResultLocation: () => confirmationPage,
})

const createPayment = async (options: PaymentFormCreateFunctionOptions) => {
  const method = options.method as PaymentMethod

  return await $httpFetch(
    '/express-replacement/funnel/resolutions/:resolutionId/payment',
    {
      method: 'POST',
      pathParams: {
        resolutionId: String(params.resolutionId),
      },
      body: {
        paymentMethod: {
          bmCode: method.bmCode,
          pspCode: method.pspCode,
          reference: method.reference,
        },
        redirectionUrls: {
          successUrl: `${origin}${confirmationPagePath}`,
          failureUrl: `${origin}${paymentPagePath}`,
          cancelUrl: `${origin}${paymentPagePath}`,
        },
      },
    },
  )
}

const trackSubmitButtonClick = () =>
  trackClick({
    zone: 'resolution_flow',
    page_type: routeName as string,
    name: routeName as string,
    orderlineId: params.orderlineId,
    resolutionId: params.resolutionId,
  })

const handleSubmitSuccess = ({ redirection }: SubmitSuccessData) => {
  proceedRedirection(redirection)
}

const goToDataProtection = async () => {
  await navigateTo(
    resolveLocalizedRoute({
      name: CMS.LEGAL_PAGE,
      params: {
        pageName: LEGAL_PAGE_NAME.DATA_PROTECTION,
      },
    }) as RouteLocationRaw,
    { open: { target: '_blank' } },
  )
}

const goToExpressReplacementTC = async () => {
  await navigateTo(
    resolveLocalizedRoute({
      name: CMS.LEGAL_PAGE,
      params: {
        pageName: LEGAL_PAGE_NAME.TERMS_EXPRESS_REPLACEMENT,
      },
    }) as RouteLocationRaw,
    { open: { target: '_blank' } },
  )
}

const exitXr = () => openModal(CUSTOMER_CARE_MODALS_NAMES.cancellationModal)
</script>
